import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, serverTimestamp, query, orderBy, where } from "firebase/firestore";
import {
    CreditCard, CheckCircle, Settings, BarChart3, Search, Filter, MoreVertical, Plus, X, Trash2, Loader2, RefreshCw
} from "lucide-react";

export default function SubscriptionManager() {
    const [plans, setPlans] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Modal State
    const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [savingPlan, setSavingPlan] = useState(false);

    // Assign Plan Modal State
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [assigningTenant, setAssigningTenant] = useState(null);
    const [assignFormData, setAssignFormData] = useState({ planId: "", status: "active" });
    const [savingAssignment, setSavingAssignment] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: "",
        priceMonthly: 0,
        description: "",
        features: "",
        entryLimit: 0,
        userLimit: 0,
        status: "active"
    });

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const plansQ = query(collection(db, "plans"), orderBy("priceMonthly", "asc"));
            const subsQ = collection(db, "subscriptions");
            const tenantsQ = query(collection(db, "companies"), where("type", "==", "tenant"));

            const [plansSnap, subsSnap, tenantsSnap] = await Promise.all([
                getDocs(plansQ),
                getDocs(subsQ),
                getDocs(tenantsQ)
            ]);

            const pData = [];
            const pMap = {};
            plansSnap.forEach(d => {
                const data = d.data();
                pData.push({ id: d.id, ...data });
                pMap[d.id] = data;
            });
            setPlans(pData);

            const tenantsMap = {};
            tenantsSnap.forEach(d => {
                tenantsMap[d.id] = d.data().name || "Unknown Tenant";
            });

            const invData = [];
            subsSnap.forEach(d => {
                const sub = d.data();
                const planDetails = pMap[sub.planId] || { name: "Unknown Plan", entryLimit: 200, userLimit: 5 };
                invData.push({
                    id: d.id,
                    tenantId: sub.tenantId,
                    tenantName: tenantsMap[sub.tenantId] || "Unknown Tenant",
                    planName: planDetails.name,
                    status: sub.status || "active",
                    usageEntries: sub.usageEntries || 0,
                    entryLimit: planDetails.entryLimit,
                    renewalDate: sub.renewalDate ? sub.renewalDate.toDate().toLocaleDateString() : "N/A"
                });
            });

            setInventory(invData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenConfigure = (plan = null) => {
        if (plan) {
            setEditingPlan(plan);
            setFormData({
                name: plan.name || "",
                priceMonthly: plan.priceMonthly || 0,
                description: plan.description || "",
                features: (plan.features || []).join(", "),
                entryLimit: plan.entryLimit || -1,
                userLimit: plan.userLimit || -1,
                status: plan.status || "active"
            });
        } else {
            setEditingPlan(null);
            setFormData({
                name: "",
                priceMonthly: 0,
                description: "",
                features: "",
                entryLimit: 200,
                userLimit: 5,
                status: "active"
            });
        }
        setIsConfigureModalOpen(true);
    };

    const handleSavePlan = async (e) => {
        e.preventDefault();
        setSavingPlan(true);
        try {
            const planPayload = {
                name: formData.name,
                priceMonthly: Number(formData.priceMonthly),
                description: formData.description,
                features: formData.features.split(",").map(f => f.trim()).filter(f => f.length > 0),
                entryLimit: Number(formData.entryLimit),
                userLimit: Number(formData.userLimit),
                status: formData.status
            };

            if (editingPlan) {
                await updateDoc(doc(db, "plans", editingPlan.id), planPayload);
            } else {
                planPayload.createdAt = serverTimestamp();
                await addDoc(collection(db, "plans"), planPayload);
            }
            setIsConfigureModalOpen(false);
            fetchData();
        } catch (error) {
            console.error("Error saving plan:", error);
            alert("Failed to save plan.");
        } finally {
            setSavingPlan(false);
        }
    };

    const handleDeletePlan = async (id) => {
        if (window.confirm("Are you sure you want to completely delete this plan? This will break subscriptions using it unless they are reassigned.")) {
            try {
                await deleteDoc(doc(db, "plans", id));
                setIsConfigureModalOpen(false);
                fetchData();
            } catch (err) {
                console.error("Error deleting plan:", err);
                alert("Failed to delete plan.");
            }
        }
    };
    const handleOpenAssign = (row) => {
        setAssigningTenant(row);
        const matchedPlan = plans.find(p => p.name === row.planName);
        setAssignFormData({
            planId: matchedPlan ? matchedPlan.id : (plans.length > 0 ? plans[0].id : ""),
            status: row.status || "active"
        });
        setIsAssignModalOpen(true);
    };

    const handleSaveAssignment = async (e) => {
        e.preventDefault();
        setSavingAssignment(true);
        try {
            const subPayload = {
                tenantId: assigningTenant.tenantId,
                planId: assignFormData.planId,
                status: assignFormData.status,
                renewalDate: serverTimestamp(), // Default reset
                usageEntries: assigningTenant.usageEntries || 0
            };

            if (assigningTenant.id) {
                await updateDoc(doc(db, "subscriptions", assigningTenant.id), subPayload);
            } else {
                subPayload.startDate = serverTimestamp();
                await addDoc(collection(db, "subscriptions"), subPayload);
            }
            setIsAssignModalOpen(false);
            fetchData();
        } catch (error) {
            console.error("Error saving assignment: ", error);
            alert("Failed to update subscription.");
        } finally {
            setSavingAssignment(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto py-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">Subscription Engine</h1>
                    <p className="text-slate-500 mt-1">Define platform plans, adjust usage limits, and monitor recurring revenue.</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => fetchData()} disabled={refreshing} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-50 transition-all disabled:opacity-50">
                        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                    </button>
                    <button onClick={() => handleOpenConfigure()} className="px-5 py-2.5 bg-navy-900 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20">
                        <Plus size={18} /> Add New Plan
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {loading ? (
                    <div className="col-span-3 py-12 flex justify-center items-center text-slate-400">
                        <Loader2 className="animate-spin mr-2" /> Loading platform subscription plans...
                    </div>
                ) : plans.length === 0 ? (
                    <div className="col-span-3 py-12 text-center text-slate-500 font-bold bg-white rounded-2xl border border-dashed border-slate-300">
                        No subscription plans configured yet. Click 'Add New Plan' to start.
                    </div>
                ) : plans.map((plan) => {
                    const isEnterprise = plan.name.toLowerCase().includes("enterprise") || plan.priceMonthly > 200;
                    const color = isEnterprise ? "bg-navy-900 text-white border-navy-800" :
                        plan.priceMonthly < 120 ? "bg-blue-50 text-blue-700 border-blue-100" :
                            "bg-violet-50 text-violet-700 border-violet-100";

                    return (
                        <div key={plan.id} className={`p-8 rounded-3xl border-2 transition-all hover:scale-[1.02] relative group ${color} ${isEnterprise ? 'shadow-2xl shadow-navy-900/30' : 'shadow-sm'}`}>

                            <button
                                onClick={() => handleOpenConfigure(plan)}
                                className={`absolute top-4 right-4 p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity ${isEnterprise ? 'bg-navy-800 text-white hover:bg-navy-700' : 'bg-white text-navy-900 shadow hover:bg-slate-50'}`}
                            >
                                <Settings size={16} />
                            </button>

                            <div className="flex justify-between items-start mb-6 w-10/12">
                                <div className={`p-3 rounded-2xl ${isEnterprise ? 'bg-navy-800' : 'bg-white/80'}`}>
                                    <CreditCard size={24} />
                                </div>
                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${isEnterprise ? 'bg-emerald-500/20 text-emerald-400' : 'bg-navy-900/10 text-navy-900'}`}>
                                    {plan.status === 'disabled' ? 'DISABLED' : (isEnterprise ? 'Popular' : 'Active Plan')}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                            <div className="flex items-baseline gap-1 mb-8">
                                <span className="text-4xl font-bold">${plan.priceMonthly}</span>
                                <span className="text-sm opacity-60">/ month</span>
                            </div>
                            <div className="space-y-4 mb-8 h-32 overflow-y-auto custom-scrollbar">
                                {plan.features && plan.features.map((f, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm font-medium">
                                        <CheckCircle size={16} className={isEnterprise ? 'text-emerald-400 shrink-0' : 'text-navy-900 shrink-0'} /> <span className="truncate">{f}</span>
                                    </div>
                                ))}
                            </div>
                            <div className={`pt-6 border-t ${isEnterprise ? 'border-navy-800' : 'border-current/10'} opacity-80 text-xs font-bold uppercase tracking-wider`}>
                                Limits: {plan.entryLimit === -1 ? '∞' : plan.entryLimit} Entries • {plan.userLimit === -1 ? '∞' : plan.userLimit} Users
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Subscription List */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <h3 className="font-bold text-navy-900 flex items-center gap-2">
                        <BarChart3 size={20} className="text-navy-900" /> Subscription Inventory
                    </h3>
                    <div className="flex gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input type="text" placeholder="Search tenant..." className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10" />
                        </div>
                        <button className="p-2 border border-slate-200 rounded-xl text-slate-400 hover:text-navy-900 transition-colors">
                            <Filter size={18} />
                        </button>
                    </div>
                </div>
                <table className="w-full text-left hidden md:table">
                    <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/30">
                        <tr>
                            <th className="px-6 py-4">Tenant</th>
                            <th className="px-6 py-4">Current Plan</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Usage (Entries)</th>
                            <th className="px-6 py-4">Next Bill</th>
                            <th className="px-6 py-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-400"><Loader2 className="animate-spin inline-block mr-2" /> Loading subscriptions...</td></tr>
                        ) : inventory.length === 0 ? (
                            <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500 font-bold border-dashed border-2 border-slate-200">No subscriptions found. Tenants need to be assigned a plan.</td></tr>
                        ) : inventory.map((row, idx) => {
                            const usagePct = row.entryLimit === -1 ? 0 : Math.min(100, (row.usageEntries / row.entryLimit) * 100);
                            return (
                                <tr key={row.id || idx} className="hover:bg-slate-50 transition-colors cursor-pointer group">
                                    <td className="px-6 py-4 font-bold text-navy-900">{row.tenantName}</td>
                                    <td className="px-6 py-4 text-sm">
                                        <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg font-bold text-xs uppercase">{row.planName}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`flex items-center gap-1.5 text-xs font-bold ${row.status === 'active' ? 'text-emerald-600' :
                                            row.status === 'cancelled' ? 'text-red-600' : 'text-amber-600'
                                            }`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${row.status === 'active' ? 'bg-emerald-500' :
                                                row.status === 'cancelled' ? 'bg-red-500' : 'bg-amber-500'
                                                }`}></div>
                                            {row.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="w-32">
                                            <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1 uppercase">
                                                <span>Usage ({row.usageEntries} / {row.entryLimit === -1 ? '∞' : row.entryLimit})</span>
                                                <span>{usagePct > 90 ? 'Limit Warning' : ''}</span>
                                            </div>
                                            <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${row.status === 'active' ? 'bg-navy-900' : 'bg-slate-300'}`} style={{ width: `${usagePct}%` }}></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium text-slate-500">{row.renewalDate}</td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleOpenAssign(row); }}
                                            className="px-3 py-1.5 bg-navy-50 text-navy-900 rounded-lg text-xs font-bold hover:bg-navy-100 transition-colors"
                                        >
                                            Change Plan
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Mobile Cards */}
                <div className="block md:hidden p-4 space-y-4">
                    {loading ? (
                        <div className="py-12 text-center text-slate-400"><Loader2 className="animate-spin inline-block mr-2" /> Loading subscriptions...</div>
                    ) : inventory.length === 0 ? (
                        <div className="py-12 text-center text-slate-500 font-bold border-dashed border-2 border-slate-200 rounded-xl">No subscriptions found. Tenants need to be assigned a plan.</div>
                    ) : inventory.map((row, idx) => {
                        const usagePct = row.entryLimit === -1 ? 0 : Math.min(100, (row.usageEntries / row.entryLimit) * 100);
                        return (
                            <div key={row.id || idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-3 relative">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-bold text-navy-900 text-lg">{row.tenantName}</div>
                                        <div className="mt-1">
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-bold text-[10px] uppercase">{row.planName}</span>
                                        </div>
                                    </div>
                                    <span className={`flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${row.status === 'active' ? 'bg-emerald-50 text-emerald-600' : row.status === 'cancelled' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                                        <div className={`w-1.5 h-1.5 rounded-full ${row.status === 'active' ? 'bg-emerald-500' : row.status === 'cancelled' ? 'bg-red-500' : 'bg-amber-500'}`}></div>
                                        {row.status.toUpperCase()}
                                    </span>
                                </div>

                                <div className="mt-2">
                                    <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1 uppercase">
                                        <span>Usage ({row.usageEntries} / {row.entryLimit === -1 ? '∞' : row.entryLimit})</span>
                                        <span className={usagePct > 90 ? 'text-red-500' : ''}>{usagePct > 90 ? 'Limit Warning' : ''}</span>
                                    </div>
                                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${row.status === 'active' ? 'bg-navy-900' : 'bg-slate-300'}`} style={{ width: `${usagePct}%` }}></div>
                                    </div>
                                </div>

                                <div className="flex justify-between items-center pt-3 border-t border-slate-50 mt-1">
                                    <div className="text-xs">
                                        <span className="text-slate-400 font-medium block">Next Bill</span>
                                        <span className="font-medium text-slate-600">{row.renewalDate}</span>
                                    </div>
                                    <button
                                        onClick={() => handleOpenAssign(row)}
                                        className="px-3 py-2 bg-navy-50 text-navy-900 rounded-lg text-xs font-bold hover:bg-navy-100 transition-colors"
                                    >
                                        Change Plan
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Configure Plan Modal */}
            {isConfigureModalOpen && (
                <div className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex justify-between items-center z-10">
                            <div>
                                <h3 className="text-xl font-bold text-navy-900 font-serif">{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h3>
                                <p className="text-sm text-slate-500">Define billing details and platform limitations.</p>
                            </div>
                            <button onClick={() => setIsConfigureModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSavePlan} className="p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Plan Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g. Starter Basic"
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Monthly Price ($)</label>
                                    <input
                                        type="number"
                                        required min="0"
                                        value={formData.priceMonthly}
                                        onChange={(e) => setFormData({ ...formData, priceMonthly: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Description</label>
                                <textarea
                                    rows="2"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief pitch for the plan..."
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                ></textarea>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex justify-between">
                                        <span>Entries / Month</span>
                                        <span className="text-navy-400">-1 for Unlimited</span>
                                    </label>
                                    <input
                                        type="number"
                                        required min="-1"
                                        value={formData.entryLimit}
                                        onChange={(e) => setFormData({ ...formData, entryLimit: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex justify-between">
                                        <span>Max Users</span>
                                        <span className="text-navy-400">-1 for Unlimited</span>
                                    </label>
                                    <input
                                        type="number"
                                        required min="-1"
                                        value={formData.userLimit}
                                        onChange={(e) => setFormData({ ...formData, userLimit: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Features (Comma Separated)</label>
                                <textarea
                                    rows="3"
                                    placeholder="Lodge Entries, Track Inventory, Admin Map..."
                                    required
                                    value={formData.features}
                                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                                ></textarea>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 font-bold uppercase tracking-wider"
                                >
                                    <option value="active" className="text-emerald-700">Active</option>
                                    <option value="disabled" className="text-red-700">Disabled</option>
                                </select>
                            </div>

                            <div className="pt-6 border-t border-slate-100 flex justify-between items-center">
                                {editingPlan ? (
                                    <button
                                        type="button"
                                        onClick={() => handleDeletePlan(editingPlan.id)}
                                        className="text-red-500 font-bold text-sm flex items-center gap-2 hover:bg-red-50 px-4 py-2 rounded-lg transition-colors"
                                    >
                                        <Trash2 size={16} /> Delete Plan
                                    </button>
                                ) : <div></div>}

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsConfigureModalOpen(false)}
                                        className="px-6 py-3 font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={savingPlan}
                                        className="px-6 py-3 bg-navy-900 text-white font-bold rounded-xl flex items-center gap-2 hover:bg-navy-800 transition-colors shadow-lg shadow-navy-900/20 disabled:opacity-70"
                                    >
                                        {savingPlan ? <Loader2 className="animate-spin" size={18} /> : (editingPlan ? 'Save Changes' : 'Create Plan')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Assign/Change Plan Modal */}
            {isAssignModalOpen && (
                <div className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md">
                        <div className="border-b border-slate-100 p-6 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-navy-900 font-serif">Manage Subscription</h3>
                                <p className="text-sm text-slate-500">Assign plan for {assigningTenant?.tenantName}</p>
                            </div>
                            <button onClick={() => setIsAssignModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveAssignment} className="p-6 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Select Plan</label>
                                <select
                                    value={assignFormData.planId}
                                    onChange={(e) => setAssignFormData({ ...assignFormData, planId: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 font-bold"
                                >
                                    {plans.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} (${p.priceMonthly}/mo)</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Subscription Status</label>
                                <select
                                    value={assignFormData.status}
                                    onChange={(e) => setAssignFormData({ ...assignFormData, status: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 font-bold uppercase tracking-wider"
                                >
                                    <option value="active" className="text-emerald-700">Active</option>
                                    <option value="trial" className="text-amber-700">Trial</option>
                                    <option value="cancelled" className="text-red-700">Cancelled</option>
                                </select>
                            </div>

                            <div className="pt-6 border-t border-slate-100 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsAssignModalOpen(false)}
                                    className="px-6 py-3 font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingAssignment}
                                    className="px-6 py-3 bg-navy-900 text-white font-bold rounded-xl flex items-center gap-2 hover:bg-navy-800 transition-colors shadow-lg shadow-navy-900/20 disabled:opacity-70"
                                >
                                    {savingAssignment ? <Loader2 className="animate-spin" size={18} /> : 'Save Assignment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
